import {
  faBell,
  faBullhorn,
  faCheckCircle,
  faClipboardList,
  faClock,
  faComment,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import RevealDropdown from 'components/base/RevealDropdown';
import { Notification } from 'services/api/notification';
import { formatDistanceToNow, format } from 'date-fns';
import { Dropdown } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  markNotificationAsRead,
  markNotificationAsUnread,
  markNotificationAsArchived,
  markNotificationAsUnArchived
} from 'services/api/notification';
import { GET_JOB_QUERY, Job } from 'services/api/job';
import CardViewModal from 'components/modules/project-management/card-view/CardViewModal';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTaskDetails } from 'providers/TaskDetailsProvider';
// Removed individual socket connection to avoid multiple connections

export interface NotificationItemProps {
  notification: Notification;
  className?: string;
  type: 'dropdownItem' | 'pageItem';
}
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'feedback_submitted':
      return <FontAwesomeIcon icon={faBullhorn} className="text-info" />;
    case 'task_assigned':
      return (
        <FontAwesomeIcon icon={faClipboardList} className="text-primary" />
      );
    case 'task_completed':
      return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
    case 'task_updated':
      return <FontAwesomeIcon icon={faSync} className="text-warning" />;
    case 'task_reminder':
      return <FontAwesomeIcon icon={faClock} className="text-danger" />;
    case 'task_stage_reminder':
      return <FontAwesomeIcon icon={faBell} className="text-warning" />;
    case 'comment_mention':
      return <FontAwesomeIcon icon={faComment} className="text-info" />;
    default:
      return <FontAwesomeIcon icon={faBullhorn} />;
  }
};

const NotificationItem = ({
  notification,
  className,
  type
}: NotificationItemProps) => {
  const queryClient = useQueryClient();
  const [showJobModal, setShowJobModal] = useState<boolean>(false);
  const [jobData, setJobData] = useState<Job | null>(null);
  const navigate = useNavigate();
  const { openTask } = useTaskDetails();

  const actorName =
    notification.actor?.firstName || notification.actor?.lastName
      ? `${notification.actor?.firstName || ''} ${
          notification.actor?.lastName || ''
        }`.trim()
      : notification.actor?.username;

  const shouldShowActor =
    Boolean(actorName) &&
    (notification.type.startsWith('task_') ||
      notification.type.startsWith('comment_')) &&
    !(
      (notification.message || '').includes(`${actorName} `) ||
      (notification.message || '').startsWith(actorName || '')
    );

  const markAsReadMutation = useMutation(
    (id: string) => markNotificationAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notificationCount']);
        toast.success('Notification marked as read');
      }
    }
  );

  const markAsUnreadMutation = useMutation(
    (id: string) => markNotificationAsUnread(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notificationCount']);
        toast.success('Notification marked as unread');
      }
    }
  );

  const markAsArchivedMutation = useMutation(
    (id: string) => markNotificationAsArchived(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notificationCount']);
        toast.success('Notification archived');
      }
    }
  );

  const markAsUnArchivedMutation = useMutation(
    (id: string) => markNotificationAsUnArchived(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notificationCount']);
        toast.success('Notification unarchived');
      }
    }
  );

  const toggleReadStatus = () => {
    if (notification.read) {
      markAsUnreadMutation.mutate(notification.id);
    } else {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const toggleArchiveStatus = () => {
    if (notification.archived) {
      markAsUnArchivedMutation.mutate(notification.id);
    } else {
      markAsArchivedMutation.mutate(notification.id);
    }
  };

  const createdDate = new Date(notification.createdAt);
  const timeAgo = formatDistanceToNow(createdDate, { addSuffix: true });
  const formattedDate = format(createdDate, 'MMM dd, yyyy');
  const formattedTime = format(createdDate, 'hh:mm a');

  useQuery(
    ['job', notification.jobId],
    () => GET_JOB_QUERY(notification.jobId as string),
    {
      enabled: !!notification.jobId && showJobModal,
      onSuccess: response => {
        setJobData(response.data);
      }
    }
  );

  const handleNotificationClick = () => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }

    if (notification.type === 'comment_mention' && notification.jobId) {
      setShowJobModal(true);
    } else if (
      [
        'task_assigned',
        'task_completed',
        'task_updated',
        'task_reminder',
        'task_stage_reminder',
        'task_overdue'
      ].includes(notification.type) &&
      notification.taskId
    ) {
      openTask(notification.taskId);
    } else if (notification.type === 'feedback_submitted') {
      navigate('/home/apps/admin-feedback');
    }
  };

  const handleModalClose = () => {
    setShowJobModal(false);
    setJobData(null);
  };

  return (
    <>
      <div
        className={classNames(
          className,
          'py-3 border-300 notification-card position-relative',
          {
            unread: !notification.read,
            'px-4 px-lg-6': type === 'pageItem',
            'px-2 px-sm-3': type === 'dropdownItem',
            'cursor-pointer':
              (notification.type === 'comment_mention' && notification.jobId) ||
              notification.type === 'feedback_submitted' ||
              ((notification.type === 'task_assigned' ||
                notification.type === 'task_completed' ||
                notification.type === 'task_updated' ||
                notification.type === 'task_reminder' ||
                notification.type === 'task_stage_reminder' ||
                notification.type === 'task_overdue') &&
                notification.taskId)
          }
        )}
        onClick={handleNotificationClick}
      >
        <div className="d-flex align-items-center justify-content-between position-relative">
          <div className="d-flex">
            <div className="notification-icon d-flex align-items-center justify-content-center me-3 position-relative">
              <span className="fs-5">
                {getNotificationIcon(notification.type)}
              </span>
              {!notification.read && (
                <span
                  className="position-absolute top-0 start-100 translate-middle rounded-circle"
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#007bff',
                    border: '1px solid #ffffff',
                    marginTop: '2px',
                    marginLeft: '-1px',
                    boxShadow: '0 0 0 1px rgba(0, 123, 255, 0.25)'
                  }}
                >
                  <span className="visually-hidden">Unread notification</span>
                </span>
              )}
            </div>
            <div
              className={classNames('flex-1', {
                'me-sm-3': type === 'dropdownItem',
                'mt-2 me-2': type === 'pageItem'
              })}
            >
              <h4 className="fs-9 text-black">{notification.title}</h4>
              <p className="fs-9 text-1000 mb-2 mb-sm-3">
                <span>{notification.message}</span>
                <span className="ms-2 text-600 fw-bold fs-10">{timeAgo}</span>
              </p>
              {shouldShowActor && (
                <p className="fs-10 text-700 mb-2">
                  By <span className="fw-bold">{actorName}</span>
                </p>
              )}
              <p className="text-800 fs-9 mb-0">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                <span className="fw-bold">{formattedTime}</span> {formattedDate}
              </p>
            </div>
          </div>
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <RevealDropdown
              className={classNames('', {
                'd-none d-sm-block': type === 'dropdownItem'
              })}
              btnClassName="notification-dropdown-toggle"
              dropdownMenuClassName="mt-2"
            >
              <Dropdown.Item onClick={toggleReadStatus}>
                Mark as {notification.read ? 'unread' : 'read'}
              </Dropdown.Item>
              <Dropdown.Item onClick={toggleArchiveStatus}>
                {notification.archived ? 'Unarchive' : 'Archive'} notification
              </Dropdown.Item>
            </RevealDropdown>
          </div>
        </div>
      </div>

      {showJobModal && jobData && (
        <CardViewModal
          show={showJobModal}
          handleClose={handleModalClose}
          project={jobData}
          canEditJob={false}
          canAddCommentsField={true}
          canAddShopCommentsField={false}
          canAddJobStageCommentsField={false}
          isLoadingTasks={false}
          jobTasks={[]}
          scrollToTasks={false}
          setScrollToTasks={undefined}
        />
      )}
    </>
  );
};

export default NotificationItem;
